import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { useLang } from "../../context/lang-context";
import {
  sharedCartApi,
  SessionInfo,
  CartProduct,
  AddProductPayload,
} from "../../services/sharedCartApi";
import "./SharedCartPage.css";

const BASE_URL = "https://fooapp.mx";

/* ========== GraphQL ========== */

const GET_RESTAURANT = gql`
  query GetRestaurant($id: Int!) {
    getRestaurant(id: $id) {
      id
      company_name
      logo
      cover_img
      branches {
        id
        name
        hours {
          weekday
          start_hour
          end_hour
          opens
        }
      }
      products {
        id
        title
        description
        price
        has_size
        snoozed
        status_web
        images {
          id
          url
          type
        }
        category {
          id
          name
          name_en
        }
        sizes {
          id
          name
          name_en
          price
        }
        ingredients {
          id
          name
          name_en
          color
          icon
        }
        options {
          id
          id_ingredient
          price_mod
          ingredient {
            id
            name
            name_en
          }
        }
      }
    }
  }
`;

/* ========== Interfaces ========== */

interface ProductImage {
  id: string;
  url: string;
  type: string;
}

interface Category {
  id: string;
  name: string;
  name_en: string;
}

interface Size {
  id: string;
  name: string;
  name_en: string;
  price: number;
}

interface Ingredient {
  id: string;
  name: string;
  name_en: string;
  color: string | null;
  icon: string | null;
}

interface OptionIngredient {
  id: string;
  name: string;
  name_en: string;
}

interface Option {
  id: string;
  id_ingredient: string;
  price_mod: number;
  ingredient: OptionIngredient;
}

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  has_size: boolean;
  snoozed: boolean;
  status_web: boolean;
  images: ProductImage[];
  category: Category | null;
  sizes: Size[];
  ingredients: Ingredient[];
  options: Option[];
}

/* ========== Helpers ========== */

function resolveImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BASE_URL}${path}`;
}

function formatPrice(price: number | string | null | undefined): string {
  const num = Number(price) || 0;
  return `$${num.toFixed(2)}`;
}

function getGuestTokenKey(token: string): string {
  return `sc_guest_${token}`;
}

function getGuestNameKey(token: string): string {
  return `sc_guest_name_${token}`;
}

/* ========== Main Component ========== */

const SharedCartPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { lang, setLang, t } = useLang();

  // Session state
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Guest state
  const [guestToken, setGuestToken] = useState<string | null>(null);
  const [guestName, setGuestName] = useState<string>("");
  const [joinName, setJoinName] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Cart state
  const [cartProducts, setCartProducts] = useState<CartProduct[]>([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [cartExpanded, setCartExpanded] = useState(false);
  const [doneMessage, setDoneMessage] = useState(false);

  // Product modal state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedSizeId, setSelectedSizeId] = useState<string | null>(null);
  const [deselectedIngredients, setDeselectedIngredients] = useState<Set<string>>(new Set());
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [productComments, setProductComments] = useState("");
  const [addingProduct, setAddingProduct] = useState(false);

  // Fetch session
  useEffect(() => {
    if (!token) return;
    setSessionLoading(true);
    sharedCartApi
      .getSession(token)
      .then((data) => {
        setSession(data);
        // Check for existing guest token
        const saved = localStorage.getItem(getGuestTokenKey(token));
        const savedName = localStorage.getItem(getGuestNameKey(token));
        if (saved) {
          setGuestToken(saved);
          setGuestName(savedName || "");
        }
      })
      .catch((err) => {
        setSessionError(err.message || t("Sesion no encontrada", "Session not found"));
      })
      .finally(() => setSessionLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch restaurant data
  const { data: restaurantData, loading: restaurantLoading } = useQuery(GET_RESTAURANT, {
    variables: { id: session?.restaurant_id },
    skip: !session?.restaurant_id,
  });

  const restaurant = restaurantData?.getRestaurant;

  // Fetch cart products when guest is joined
  const fetchCart = useCallback(() => {
    if (!token || !guestToken) return;
    sharedCartApi
      .getMyProducts(token, guestToken)
      .then((data) => {
        setCartProducts(data.products || []);
        setCartTotal(data.total || 0);
      })
      .catch(() => {});
  }, [token, guestToken]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Group products by category
  const groupedProducts = useMemo(() => {
    if (!restaurant?.products) return {};
    const groups: Record<string, Product[]> = {};
    restaurant.products
      .filter((p: Product) => p.status_web && !p.snoozed)
      .forEach((product: Product) => {
        const catName = product.category
          ? lang === "es"
            ? product.category.name
            : product.category.name_en || product.category.name
          : lang === "es"
          ? "Otros"
          : "Other";
        if (!groups[catName]) groups[catName] = [];
        groups[catName].push(product);
      });
    return groups;
  }, [restaurant, lang]);

  // Compute modal product price
  const computedPrice = useMemo(() => {
    if (!selectedProduct) return 0;
    let price = selectedProduct.price;

    // If size selected, use size price
    if (selectedSizeId) {
      const size = selectedProduct.sizes.find((s) => s.id === selectedSizeId);
      if (size) price = size.price;
    } else if (selectedProduct.has_size && selectedProduct.sizes.length > 0) {
      // Default to first size price if product has sizes
      price = selectedProduct.sizes[0].price;
    }

    // Add selected option price_mods
    selectedOptions.forEach((optId) => {
      const opt = selectedProduct.options.find((o) => o.id === optId);
      if (opt) price += opt.price_mod;
    });

    return price;
  }, [selectedProduct, selectedSizeId, selectedOptions]);

  /* ========== Handlers ========== */

  const handleJoin = async () => {
    if (!token || !joinName.trim()) return;
    setJoining(true);
    setJoinError(null);
    try {
      const result = await sharedCartApi.join(token, joinName.trim());
      localStorage.setItem(getGuestTokenKey(token), result.guest_token);
      localStorage.setItem(getGuestNameKey(token), result.guest_name);
      setGuestToken(result.guest_token);
      setGuestName(result.guest_name);
    } catch (err: any) {
      setJoinError(err.message || t("Error al unirte", "Error joining"));
    } finally {
      setJoining(false);
    }
  };

  const openProductModal = (product: Product) => {
    setSelectedProduct(product);
    // Default: first size selected if product has sizes
    if (product.has_size && product.sizes.length > 0) {
      setSelectedSizeId(product.sizes[0].id);
    } else {
      setSelectedSizeId(null);
    }
    // All ingredients selected by default (deselected set is empty)
    setDeselectedIngredients(new Set());
    setSelectedOptions(new Set());
    setProductComments("");
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
  };

  const toggleIngredient = (id: string) => {
    setDeselectedIngredients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleOption = (id: string) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddProduct = async () => {
    if (!token || !guestToken || !selectedProduct) return;
    setAddingProduct(true);
    try {
      // Build ingredients_txt for display
      const activeIngredients = selectedProduct.ingredients
        .filter((ing) => !deselectedIngredients.has(ing.id))
        .map((ing) => (lang === "es" ? ing.name : ing.name_en || ing.name));
      const activeOptions = selectedProduct.options
        .filter((opt) => selectedOptions.has(opt.id))
        .map((opt) =>
          lang === "es"
            ? opt.ingredient.name
            : opt.ingredient.name_en || opt.ingredient.name
        );
      const ingredientsTxt = [...activeIngredients, ...activeOptions].join(", ");

      const payload: AddProductPayload = {
        guest_token: guestToken,
        product_id: parseInt(selectedProduct.id),
        size: selectedSizeId ? parseInt(selectedSizeId) : null,
        ingredients: Array.from(deselectedIngredients).map((id) => parseInt(id)),
        ingredients_opt: Array.from(selectedOptions).map((id) => parseInt(id)),
        ingredients_txt: ingredientsTxt,
        comments: productComments || undefined,
      };

      await sharedCartApi.addProduct(token, payload);
      closeProductModal();
      fetchCart();
    } catch (err: any) {
      alert(err.message || t("Error al agregar producto", "Error adding product"));
    } finally {
      setAddingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!token || !guestToken) return;
    try {
      await sharedCartApi.deleteProduct(token, guestToken, productId);
      fetchCart();
    } catch (err: any) {
      alert(err.message || t("Error al eliminar", "Error deleting"));
    }
  };

  const handleDone = () => {
    setDoneMessage(true);
    setCartExpanded(false);
  };

  /* ========== Renders ========== */

  // Loading
  if (sessionLoading) {
    return (
      <div className="sc-loading">
        <div className="sc-spinner" />
      </div>
    );
  }

  // Error / expired
  if (sessionError || !session) {
    return (
      <div className="sc-error">
        <img src="/img/logo_svg.svg" alt="Foo" className="sc-error-logo" />
        <h2>{t("Enlace no valido", "Invalid link")}</h2>
        <p>
          {sessionError ||
            t(
              "Esta sesion no existe o ha expirado.",
              "This session does not exist or has expired."
            )}
        </p>
        <AppStoreCTA t={t} />
      </div>
    );
  }

  // Check if expired
  const isExpired = new Date(session.expires_at) < new Date();
  if (isExpired) {
    return (
      <div className="sc-error">
        <img src="/img/logo_svg.svg" alt="Foo" className="sc-error-logo" />
        <h2>{t("Sesion expirada", "Session expired")}</h2>
        <p>
          {t(
            "Esta sesion compartida ha expirado.",
            "This shared session has expired."
          )}
        </p>
        <AppStoreCTA t={t} />
      </div>
    );
  }

  // Done message
  if (doneMessage) {
    return (
      <div className="sc-done">
        <img src="/img/logo_svg.svg" alt="Foo" className="sc-done-logo" />
        <div className="sc-done-icon">
          <i className="fas fa-check-circle"></i>
        </div>
        <h2>{t("Listo!", "Done!")}</h2>
        <p>
          {t(
            `Tus productos fueron agregados al carrito de ${session.host_name}. Cuando todos terminen, ${session.host_name} enviara el pedido.`,
            `Your products were added to ${session.host_name}'s cart. Once everyone is done, ${session.host_name} will place the order.`
          )}
        </p>
        <p className="sc-done-summary">
          {cartProducts.length}{" "}
          {t("productos", "products")} &middot; {formatPrice(cartTotal)}
        </p>
      </div>
    );
  }

  const logoUrl = resolveImageUrl(session.restaurant_logo);

  // Join screen
  if (!guestToken) {
    return (
      <div className="sc-join">
        <div className="sc-join-card">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={session.restaurant_name}
              className="sc-join-logo"
            />
          )}
          <h1 className="sc-join-restaurant">{session.restaurant_name}</h1>
          <p className="sc-join-invite">
            <strong>{session.host_name}</strong>{" "}
            {t(
              "te invita a agregar productos",
              "invites you to add products"
            )}
          </p>
          <p className="sc-join-guests">
            {session.guest_count}/{session.max_guests}{" "}
            {t("invitados", "guests")}
          </p>

          <div className="sc-join-form">
            <input
              type="text"
              className="sc-join-input"
              placeholder={t("Tu nombre", "Your name")}
              value={joinName}
              onChange={(e) => setJoinName(e.target.value.slice(0, 20))}
              maxLength={20}
              onKeyDown={(e) => e.key === "Enter" && handleJoin()}
            />
            <button
              className="sc-join-btn"
              onClick={handleJoin}
              disabled={!joinName.trim() || joining}
            >
              {joining ? (
                <span className="sc-btn-spinner" />
              ) : (
                t("Unirme", "Join")
              )}
            </button>
          </div>
          {joinError && <p className="sc-join-error">{joinError}</p>}
        </div>

        <div className="sc-join-footer">
          <img src="/img/logo_svg.svg" alt="Foo" className="sc-join-footer-logo" />
          <span>{t("Impulsado por", "Powered by")} <strong>Foo</strong></span>
        </div>
      </div>
    );
  }

  // Menu view
  const coverUrl = resolveImageUrl(restaurant?.cover_img);
  const restLogoUrl = resolveImageUrl(restaurant?.logo);

  return (
    <div className="sc-page">
      {/* Nav */}
      <nav className="sc-nav">
        <div className="container d-flex align-items-center justify-content-between">
          <img src="/img/logo_svg.svg" alt="Foo" className="sc-nav-logo" />
          <div className="sc-nav-actions">
            <span className="sc-guest-badge">
              <i className="fas fa-user"></i> {guestName}
            </span>
            <button
              className="btn btn-outline-dark btn-sm sc-lang-btn"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
          </div>
        </div>
      </nav>

      {/* Restaurant header */}
      {restaurantLoading ? (
        <div className="sc-restaurant-loading">
          <div className="sc-spinner" />
        </div>
      ) : (
        <>
          <header
            className="sc-cover"
            style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : {}}
          >
            <div className="sc-cover-overlay" />
            <div className="container sc-cover-content">
              {restLogoUrl && (
                <img
                  src={restLogoUrl}
                  alt={session.restaurant_name}
                  className="sc-cover-logo"
                />
              )}
              <div>
                <h1 className="sc-cover-name">{session.restaurant_name}</h1>
                <p className="sc-cover-host">
                  {t("Pedido de", "Order by")} {session.host_name}
                </p>
              </div>
            </div>
          </header>

          {/* Menu */}
          <div className="container sc-menu">
            <h2 className="sc-section-title">{t("Menu", "Menu")}</h2>

            {Object.keys(groupedProducts).length === 0 && (
              <p className="sc-empty">
                {t("No hay productos disponibles.", "No products available.")}
              </p>
            )}

            {Object.entries(groupedProducts).map(([category, products]) => (
              <div key={category} className="sc-category">
                <h3 className="sc-category-title">{category}</h3>
                <div className="sc-products">
                  {products.map((product: Product) => {
                    const image =
                      product.images?.find((img) => img.type === "cover") ||
                      product.images?.[0];
                    const imageUrl = resolveImageUrl(image?.url);

                    const priceLabel =
                      product.has_size && product.sizes.length > 0
                        ? `${t("Desde", "From")} ${formatPrice(
                            Math.min(...product.sizes.map((s) => s.price))
                          )}`
                        : formatPrice(product.price);

                    return (
                      <div
                        key={product.id}
                        className="sc-product"
                        onClick={() => openProductModal(product)}
                      >
                        <div className="sc-product-info">
                          <h4 className="sc-product-title">{product.title}</h4>
                          {product.description && (
                            <p className="sc-product-desc">
                              {product.description}
                            </p>
                          )}
                          <span className="sc-product-price">{priceLabel}</span>
                        </div>
                        <div className="sc-product-right">
                          {imageUrl && (
                            <img
                              src={imageUrl}
                              alt={product.title}
                              className="sc-product-img"
                            />
                          )}
                          <button
                            className="sc-add-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openProductModal(product);
                            }}
                            aria-label={t("Agregar", "Add")}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Bottom spacer for mini cart */}
            {cartProducts.length > 0 && <div className="sc-bottom-spacer" />}
          </div>
        </>
      )}

      {/* Mini Cart */}
      {cartProducts.length > 0 && (
        <div className={`sc-mini-cart ${cartExpanded ? "sc-mini-cart-expanded" : ""}`}>
          {/* Expanded product list */}
          {cartExpanded && (
            <div className="sc-mini-cart-list">
              <div className="sc-mini-cart-header">
                <h4>{t("Mis productos", "My products")}</h4>
                <button
                  className="sc-mini-cart-close"
                  onClick={() => setCartExpanded(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              {cartProducts.map((item) => (
                <div key={item.id} className="sc-cart-item">
                  <div className="sc-cart-item-info">
                    <span className="sc-cart-item-name">
                      {item.ingredients_txt || item.name}
                    </span>
                    <span className="sc-cart-item-price">
                      {formatPrice(item.price)}
                    </span>
                  </div>
                  <button
                    className="sc-cart-item-delete"
                    onClick={() => handleDeleteProduct(item.id)}
                    aria-label={t("Eliminar", "Delete")}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
              <div className="sc-cart-total-row">
                <span>{t("Total", "Total")}</span>
                <span className="sc-cart-total-price">
                  {formatPrice(cartTotal)}
                </span>
              </div>
              <button className="sc-done-btn" onClick={handleDone}>
                {t("Listo", "Done")}
              </button>
            </div>
          )}

          {/* Collapsed bar */}
          <div
            className="sc-mini-cart-bar"
            onClick={() => setCartExpanded(!cartExpanded)}
          >
            <div className="sc-mini-cart-bar-left">
              <span className="sc-cart-count">{cartProducts.length}</span>
              <span className="sc-cart-label">
                {cartProducts.length === 1
                  ? t("producto", "product")
                  : t("productos", "products")}
              </span>
            </div>
            <span className="sc-cart-bar-total">{formatPrice(cartTotal)}</span>
            <i
              className={`fas fa-chevron-${cartExpanded ? "down" : "up"}`}
            ></i>
          </div>
        </div>
      )}

      {/* Product Customization Modal */}
      {selectedProduct && (
        <div className="sc-modal-overlay" onClick={closeProductModal}>
          <div
            className="sc-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="sc-modal-close" onClick={closeProductModal}>
              <i className="fas fa-times"></i>
            </button>

            <div className="sc-modal-body">
              {/* Product image */}
              {(() => {
                const img =
                  selectedProduct.images?.find((i) => i.type === "cover") ||
                  selectedProduct.images?.[0];
                const url = resolveImageUrl(img?.url);
                return url ? (
                  <img
                    src={url}
                    alt={selectedProduct.title}
                    className="sc-modal-img"
                  />
                ) : null;
              })()}

              <div className="sc-modal-content">
                <h3 className="sc-modal-title">{selectedProduct.title}</h3>
                {selectedProduct.description && (
                  <p className="sc-modal-desc">{selectedProduct.description}</p>
                )}
                <p className="sc-modal-price">
                  {formatPrice(computedPrice)}
                </p>

                {/* Sizes */}
                {selectedProduct.has_size &&
                  selectedProduct.sizes.length > 0 && (
                    <div className="sc-modal-section">
                      <h4 className="sc-modal-section-title">
                        {t("Tamano", "Size")}
                      </h4>
                      <div className="sc-sizes">
                        {selectedProduct.sizes.map((size) => (
                          <label key={size.id} className="sc-size-option">
                            <input
                              type="radio"
                              name="product-size"
                              checked={selectedSizeId === size.id}
                              onChange={() => setSelectedSizeId(size.id)}
                            />
                            <span className="sc-size-label">
                              {lang === "es"
                                ? size.name
                                : size.name_en || size.name}{" "}
                              - {formatPrice(size.price)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Ingredients */}
                {selectedProduct.ingredients.length > 0 && (
                  <div className="sc-modal-section">
                    <h4 className="sc-modal-section-title">
                      {t("Ingredientes", "Ingredients")}
                    </h4>
                    <div className="sc-ingredients">
                      {selectedProduct.ingredients.map((ing) => (
                        <label key={ing.id} className="sc-ingredient-option">
                          <input
                            type="checkbox"
                            checked={!deselectedIngredients.has(ing.id)}
                            onChange={() => toggleIngredient(ing.id)}
                          />
                          <span className="sc-ingredient-label">
                            {lang === "es"
                              ? ing.name
                              : ing.name_en || ing.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Options */}
                {selectedProduct.options.length > 0 && (
                  <div className="sc-modal-section">
                    <h4 className="sc-modal-section-title">
                      {t("Extras", "Extras")}
                    </h4>
                    <div className="sc-options">
                      {selectedProduct.options.map((opt) => (
                        <label key={opt.id} className="sc-option-item">
                          <input
                            type="checkbox"
                            checked={selectedOptions.has(opt.id)}
                            onChange={() => toggleOption(opt.id)}
                          />
                          <span className="sc-option-label">
                            {lang === "es"
                              ? opt.ingredient.name
                              : opt.ingredient.name_en ||
                                opt.ingredient.name}
                            {opt.price_mod > 0 && (
                              <span className="sc-option-price">
                                {" "}
                                +{formatPrice(opt.price_mod)}
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments */}
                <div className="sc-modal-section">
                  <h4 className="sc-modal-section-title">
                    {t("Comentarios", "Comments")}
                  </h4>
                  <textarea
                    className="sc-comments-input"
                    placeholder={t(
                      "Instrucciones especiales...",
                      "Special instructions..."
                    )}
                    value={productComments}
                    onChange={(e) => setProductComments(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Add button */}
            <button
              className="sc-modal-add-btn"
              onClick={handleAddProduct}
              disabled={addingProduct}
            >
              {addingProduct ? (
                <span className="sc-btn-spinner" />
              ) : (
                <>
                  {t("Agregar", "Add")} {formatPrice(computedPrice)}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- App Store CTA ---------- */

const APP_STORE_URL = "https://apps.apple.com/app/foo/id0000000000"; // TODO: replace with real App Store ID
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=mx.fooapp.android";

function getStoreUrl(): string {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return APP_STORE_URL;
  return PLAY_STORE_URL;
}

const AppStoreCTA: React.FC<{ t: (es: string, en: string) => string }> = ({ t }) => {
  const url = getStoreUrl();
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || "");

  return (
    <div className="sc-app-cta">
      <a href={url} target="_blank" rel="noopener noreferrer" className="sc-app-cta-link">
        <img
          src={isIOS ? "/img/appstore-badge.png" : "/img/google-play-badge.png"}
          alt={isIOS ? "App Store" : "Google Play"}
          className="sc-app-cta-badge"
        />
      </a>
      <p className="sc-app-cta-text">
        {t(
          "Crea tu cuenta gratis en Foo para ordenar sin costo extra a tus restaurantes favoritos",
          "Create your free Foo account to order at no extra cost from your favorite restaurants"
        )}
      </p>
    </div>
  );
};

export default SharedCartPage;
